import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Loader2, Building2, Shield, Zap, Clock } from 'lucide-react';

/*
 * Altam™ Operating Services
 * Login Page
 * Copyright (c) 2026 Pecaro Consulting Inc. All rights reserved.
 */

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sessionExpiredMessage, setSessionExpiredMessage] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  // Check for session expired message
  useEffect(() => {
    if (sessionStorage.getItem('sessionExpired')) {
      setSessionExpiredMessage(true);
      sessionStorage.removeItem('sessionExpired');
      // Auto-hide after 10 seconds
      const timer = setTimeout(() => setSessionExpiredMessage(false), 10000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      setError('Please enter your email and password');
      return;
    }

    setLoading(true);
    setError('');
    setSessionExpiredMessage(false);
    const result = await login(email, password);
    setLoading(false);

    if (result.success) {
      navigate('/dashboard');
    } else {
      setError(result.message || 'Invalid credentials');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-violet-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Session Expired Banner */}
        {sessionExpiredMessage && (
          <div className="mb-4 bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center gap-3 animate-in fade-in slide-in-from-top duration-300" data-testid="session-expired-banner">
            <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
              <Clock className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="font-medium text-amber-800">Session Expired</p>
              <p className="text-sm text-amber-700">You were logged out due to inactivity. Please sign in again.</p>
            </div>
          </div>
        )}

        {/* ALTAM Logo & Branding */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-indigo-600 to-violet-600 rounded-2xl mb-4 shadow-lg">
            <Building2 className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-indigo-600 to-violet-600 bg-clip-text text-transparent">
            ALTAM™
          </h1>
          <p className="text-slate-600 mt-2 text-sm font-medium">
            Property, Finance, and Governance — Unified
          </p>
          <div className="flex items-center justify-center gap-4 mt-3 text-xs text-slate-500">
            <span className="flex items-center gap-1">
              <Shield className="w-3 h-3" /> Enterprise Ready
            </span>
            <span className="flex items-center gap-1">
              <Zap className="w-3 h-3" /> AI-Powered
            </span>
          </div>
        </div>

        <div className="bg-white/80 backdrop-blur border border-slate-200 rounded-xl shadow-xl p-6">
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-slate-900">Sign In</h2>
            <p className="text-sm text-slate-500 mt-1">Enter your credentials to access your account</p>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm" data-testid="login-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-1">
                Email
              </label>
              <input
                id="email"
                type="email"
                placeholder="name@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="login-email"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white"
              />
            </div>
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-slate-700 mb-1">
                Password
              </label>
              <input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="login-password"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white"
              />
            </div>
            <button
              type="submit"
              className="w-full py-2.5 px-4 bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 text-white font-medium rounded-lg transition-all disabled:opacity-50 flex items-center justify-center"
              disabled={loading}
              data-testid="login-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Signing in...
                </>
              ) : (
                'Sign In'
              )}
            </button>
          </form>

          <div className="mt-6 pt-4 border-t border-slate-200">
            <p className="text-xs text-slate-500 text-center">
              Forgot your password? Contact your administrator.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-8 pt-6 border-t border-slate-200/50">
          <p className="text-xs text-slate-500">
            <strong>ALTAM™ Operating Services</strong>
          </p>
          <p className="text-xs text-slate-400 mt-1">
            © 2026 Pecaro Consulting Inc. All rights reserved.
          </p>
          <p className="text-xs text-slate-300 mt-1">
            ALTAM™ is a registered trademark of Pecaro Consulting Inc.
          </p>
          <p className="text-xs text-slate-400 mt-2">
            <a href="mailto:info@pecaroconsulting.com" className="text-indigo-500 hover:underline">Contact Sales</a>
            {' • '}
            <Link to="/terms" className="text-indigo-500 hover:underline">Terms of Service</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
